import React, { useContext, useEffect, useState } from "react";
import { Flex } from "theme-ui";
import Button from "../../components/Buttons/Button";
import BackgroundImage from "../../components/Images/BackgroundImage";
import { graphql, useStaticQuery } from "gatsby";
import SlantSection from "../../components/Slant/Slant";
import Carousel from "../../components/Carousel";
import TrustPilotReviews from "../../components/Trustpilot/TrustpilotReviews";
import { formatReviewName } from "../../utils/utils";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";

const Arrow = ({
  direction,
  moveSlide,
  arrowColor = "black",
}: {
  direction: "left" | "right";
  moveSlide: (value: string) => void;
  arrowColor?: string;
}) => {
  return direction === "left" ? (
    <svg
      width="40"
      height="30"
      viewBox="0 0 50 232"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={(e: Event) => moveSlide(e)}
      sx={{
        position: "relative",
        zIndex: 9,
        left: [0, null, null, -30, -40],
        cursor: "pointer",
      }}
    >
      <g>
        <path
          d="M4 112L107.5 0.282715L107.5 223.717L4 112Z"
          fill={arrowColor}
        />
      </g>
    </svg>
  ) : (
    <svg
      width="40"
      height="30"
      viewBox="0 0 50 232"
      fill="none"
      sx={{
        position: "relative",
        zIndex: 9,
        cursor: "pointer",
        right: ["5px", null, null, -10, -20],
      }}
      xmlns="http://www.w3.org/2000/svg"
      onClick={(e: Event) => moveSlide(e)}
    >
      <g>
        <path
          d="M108 112L4.49988 223.717L4.49988 0.282724L108 112Z"
          fill={arrowColor}
        />
      </g>
    </svg>
  );
};

const BackgroundImageWrapper = ({ children, data }) => {
  return (
    <>
      <BackgroundImage
        image={data.bg.childImageSharp.fluid}
        sx={{
          width: ["100%"],
          display: ["none", null, null, "block"],
        }}
      >
        {children}
      </BackgroundImage>
      <div
        sx={{
          display: ["block", null, null, "none"],
          width: "100%",
        }}
      >
        {children}
      </div>
    </>
  );
};

const TrustPilotBlock = () => {
  const data = useStaticQuery(
    graphql`
      query TrustPilotBlockBlockQuery {
        bg: file(relativePath: { eq: "home/trustpilotbgimage.jpg" }) {
          childImageSharp {
            fluid(quality: 90, maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        allDataJson {
          edges {
            node {
              blocks {
                TrustPilotBlock {
                  heading
                  button1 {
                    text
                    href
                    variant
                  }
                  button2 {
                    text
                    href
                    variant
                  }
                }
              }
            }
          }
        }
        allTrustPilotAllReviews {
          edges {
            node {
              reviews {
                numberOfLikes
                stars
                text
                title
                createdAt
                isVerified
                reviewVerificationLevel
                consumer {
                  displayName
                }
              }
            }
          }
        }
      }
    `
  );

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 5000);
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on("dragStarted", clearNextTimeout);
        slider.on("animationEnded", nextTimeout);
        slider.on("updated", nextTimeout);
      },
    ]
  );

  const {
    TrustPilotBlock: { heading, button1, button2 },
  } = data.allDataJson.edges[0].node.blocks;
  const { allTrustPilotAllReviews } = data;

  return (
    <SlantSection
      background="white"
      variant="full"
      sx={{
        padding: 0,
        px: "0!important",
        zIndex: 3,
      }}
    >
      <BackgroundImageWrapper {...{ data }}>
        <div
          sx={{
            bg: ["rgba(255,255,255, .8)"],
          }}
        >
          <div
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: ["100%"],
              height: "100%",
              py: [50, 100, null, null, "100px"],
              pb: [50, 100, null, null, 200],
              px: [20, null, null, null, 135],
            }}
            data-sal={"slide-up"}
            data-sal-easing="easeOutQuart"
            data-sal-duration={1000}
            data-sal-delay="200"
          >
            <TrustPilotReviews level="Excellent" />
            <h2
              sx={{
                color: "brand.darkBlue",
                fontWeight: 600,
                textAlign: "center",
              }}
              dangerouslySetInnerHTML={{ __html: heading }}
            />
            <div
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                py: 0,
                px: [0, null, null, 4],
              }}
            >
              <Arrow
                arrowColor={"#2AA2DA"}
                direction="left"
                moveSlide={(e: any) =>
                  e.stopPropagation() || instanceRef.current?.prev()
                }
              />

              <div ref={sliderRef} className="keen-slider">
                {allTrustPilotAllReviews.edges[0].node.reviews.map(
                  (review: any, i: number) => (
                    <div
                      key={i}
                      className="keen-slider__slide"
                      sx={{
                        position: "relative",
                        width: "100%",
                        px: 3,
                        // height: [600, null, null, 440],
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <h3
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {review.title}
                      </h3>
                      <p
                        sx={{
                          lineHeight: [1.5],
                        }}
                      >
                        {review.text}
                      </p>
                      <p
                        sx={{
                          p: 0,
                          m: 0,
                        }}
                      >
                        <em>
                          {formatReviewName(review.consumer.displayName)}{" "}
                          {new Intl.DateTimeFormat("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }).format(new Date(review.createdAt))}
                        </em>
                      </p>
                    </div>
                  )
                )}
              </div>
              <Arrow
                arrowColor={"#2AA2DA"}
                direction="right"
                moveSlide={(e: any) =>
                  e.stopPropagation() || instanceRef.current?.next()
                }
              />
            </div>

            <Flex
              sx={{
                flexWrap: "wrap",
                justifyContent: "center",
                mt: 3,
                mb: [5, null, null, 0],
              }}
            >
              <Button {...button1} sx={{ mx: 3 }} />
              <Button {...button2} sx={{ mx: 3 }} />
            </Flex>
          </div>
        </div>
      </BackgroundImageWrapper>
    </SlantSection>
  );
};

export default TrustPilotBlock;
